import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RRule } from 'rrule';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { t } from '../../i18n/nl.js';
import ConfirmModal from '../../components/ConfirmModal';
import CoachAvailabilityCalendar from '../../components/CoachAvailabilityCalendar.jsx';
import { sendPushToClient } from '../../utils/pushUtils.js';
import {
  toAmsterdamISO, todayStr, nowTimeStr,
  amsterdamDateStr, firstOccurrenceDate, toAmsterdamDate,
} from '../../utils/dateUtils.js';

const JS_TO_DAYKEY = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

const WEEKDAGEN = [
  { key: 'ma', label: t('lesAanmaken.weekdagen.ma'), rruleDay: RRule.MO, jsIndex: 1 },
  { key: 'di', label: t('lesAanmaken.weekdagen.di'), rruleDay: RRule.TU, jsIndex: 2 },
  { key: 'wo', label: t('lesAanmaken.weekdagen.wo'), rruleDay: RRule.WE, jsIndex: 3 },
  { key: 'do', label: t('lesAanmaken.weekdagen.do'), rruleDay: RRule.TH, jsIndex: 4 },
  { key: 'vr', label: t('lesAanmaken.weekdagen.vr'), rruleDay: RRule.FR, jsIndex: 5 },
  { key: 'za', label: t('lesAanmaken.weekdagen.za'), rruleDay: RRule.SA, jsIndex: 6 },
  { key: 'zo', label: t('lesAanmaken.weekdagen.zo'), rruleDay: RRule.SU, jsIndex: 0 },
];

function occurrenceToStartsAt(occDate, dayTimes) {
  const dateStr = amsterdamDateStr(occDate.toISOString());
  const dayKey = JS_TO_DAYKEY[new Date(`${dateStr}T12:00:00Z`).getUTCDay()];
  return toAmsterdamISO(dateStr, dayTimes[dayKey] || '09:00');
}

function getPreviewDayKey(occ) {
  const dateStr = amsterdamDateStr(occ.toISOString());
  return JS_TO_DAYKEY[new Date(`${dateStr}T12:00:00Z`).getUTCDay()];
}

export default function LesAanmakenPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();

  const [naam, setNaam] = useState('');
  const [beschrijving, setBeschrijving] = useState('');
  const [locationId, setLocationId] = useState('');
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
  });
  const [lesTypeKind, setLesTypeKind] = useState('group'); // 'group' | 'personal' | 'workshop'
  const isPT = lesTypeKind === 'personal';
  const isWorkshop = lesTypeKind === 'workshop';
  const [isPtPlus, setIsPtPlus] = useState(false);
  const [secondClientId, setSecondClientId] = useState('');
  const [ptPlusWarnings, setPtPlusWarnings] = useState([]);
  const [coachId, setCoachId] = useState('');
  const [clientId, setClientId] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [datum, setDatum] = useState(todayStr());
  const [tijd, setTijd] = useState(nowTimeStr());
  const [duur, setDuur] = useState('60');
  const [maxDeelnemers, setMaxDeelnemers] = useState('10');
  const [annuleringstermijn, setAnnuleringstermijn] = useState('24');
  const [creditCost, setCreditCost] = useState('1');
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [dayTimes, setDayTimes] = useState({});
  const [recurStartDate, setRecurStartDate] = useState(todayStr());
  const [recurEndDate, setRecurEndDate] = useState('');
  const [isInfinite, setIsInfinite] = useState(false);

  const pendingClientId = useRef(null);

  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [clients, setClients] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedMembershipTypes, setSelectedMembershipTypes] = useState(new Set());
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [saveLocConfirmOpen, setSaveLocConfirmOpen] = useState(false);
  const pendingSaveLocRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [{ data: locs }, { data: coachs }, { data: memTypes }] = await Promise.all([
        supabase.from('locations').select('id, name').eq('is_active', true).order('name'),
        supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach']).is('deleted_at', null).order('full_name'),
        supabase.from('membership_types').select('id, name').eq('is_active', true).order('name'),
      ]);
      setLocations(locs || []);
      setCoaches(coachs || []);
      setMembershipTypes(memTypes || []);
      if (locs?.length === 1) setLocationId(locs[0].id);
      // BUG 8: niet instellen als prefillPT al een coachId meegaf (prefill-effect loopt parallel)
      if (user?.id && !location.state?.prefillPT) setCoachId(user.id);
    } catch (err) {
      setError(t('lesAanmaken.foutLaden') + err.message);
    } finally {
      setLoadingInit(false);
    }
  }, [user?.id, location.state]);

  useEffect(() => { loadData(); }, [loadData]);

  // Verwerk navigatieparameters (voor vooringevulde PT-afspraak vanuit ClientDetailPage)
  useEffect(() => {
    if (location.state?.prefillPT) {
      const { type, clientId: prefilledClientId, coachId: prefilledCoachId, clientName, prefillTrial } = location.state;
      if (type === 'personal') {
        setLesTypeKind('personal');
        if (prefilledCoachId) setCoachId(prefilledCoachId);
        // Sla clientId op in ref zodat het wordt ingesteld NADAT clients geladen zijn.
        // Direct setClientId hier werkt niet: de [isPT, coachId] effect runt op dezelfde
        // render-cyclus en wint met setClientId('') in de else-branch.
        if (prefilledClientId) pendingClientId.current = prefilledClientId;
        if (clientName) setNaam(`PT - ${clientName}`);
        if (prefillTrial) {
          setIsTrial(true);
          setCreditCost('0');
        }
      }
    }
  }, [location.state]);

  const loadClientsForCoach = useCallback(async (coachIdToLoad) => {
    if (!coachIdToLoad) {
      setClients([]);
      return;
    }
    setLoadingClients(true);
    try {
      const { data, error: err } = await supabase
        .from('coach_clients')
        .select('client:profiles!coach_clients_client_id_fkey(id, full_name, client_status, customer_number, deleted_at)')
        .eq('coach_id', coachIdToLoad)
        .in('status', ['active', 'inactive']);
      if (err) throw err;
      const clientList = (data || []).map(r => r.client).filter(c => c && !c.deleted_at).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setClients(clientList);
      if (pendingClientId.current) {
        setClientId(pendingClientId.current);
        pendingClientId.current = null;
      }
    } catch (err) {
      console.error('Fout bij laden clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    if (isPT && coachId) {
      loadClientsForCoach(coachId);
    } else {
      setClients([]);
      setClientId('');
    }
  }, [isPT, coachId, loadClientsForCoach]);

  // BUG 9: Bij prospects automatisch creditCost op 0 zetten (geen actief abonnement)
  useEffect(() => {
    if (!isPT || !clientId) return;
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient?.client_status === 'prospect') {
      setCreditCost('0');
      setIsTrial(true); // Automatisch isTrial aanvinken voor prospects
    }
  }, [clientId, clients, isPT]);

  // 1-op-2 PT: waarschuwingen controleren bij selectie tweede client
  useEffect(() => {
    if (!isPtPlus || !secondClientId) {
      setPtPlusWarnings([]);
      return;
    }
    let cancelled = false;
    async function checkWarnings() {
      const warnings = [];
      const { data: membership } = await supabase
        .from('client_memberships')
        .select('id, current_pt_plus_credits, membership_type_id, membership_types!inner(name, session_duration_minutes)')
        .eq('client_id', secondClientId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!membership) {
        warnings.push({ type: 'W1', msg: t('lesAanmaken.ptPlusWarnGeenAbo') });
      } else {
        if ((membership.current_pt_plus_credits || 0) < (parseInt(creditCost) || 1)) {
          warnings.push({ type: 'W3', msg: t('lesAanmaken.ptPlusWarnCredits') });
        }
        const aboMinutes = membership.membership_types?.session_duration_minutes;
        const selectedDuur = parseInt(duur) || 60;
        if (aboMinutes && aboMinutes !== selectedDuur) {
          warnings.push({ type: 'W2', msg: t('lesAanmaken.ptPlusWarnDuur').replace('{abo}', aboMinutes).replace('{gekozen}', selectedDuur) });
        }
      }
      if (!cancelled) setPtPlusWarnings(warnings);
    }
    checkWarnings();
    return () => { cancelled = true; };
  }, [isPtPlus, secondClientId, creditCost, duur]);

  useEffect(() => {
    if (!isRecurring || selectedDays.size === 0) { setPreview([]); return; }
    try {
      const selectedList = WEEKDAGEN.filter(d => selectedDays.has(d.key));
      const byweekday = selectedList.map(d => d.rruleDay);
      const firstOccs = selectedList.map(d => firstOccurrenceDate(recurStartDate, d.jsIndex));
      const dtstart = firstOccs.reduce((e, o) => o < e ? o : e);
      const until = isInfinite
        ? new Date(dtstart.getTime() + 84 * 86400000)
        : (recurEndDate ? new Date(toAmsterdamISO(recurEndDate, '23:59')) : null);
      if (!until) { setPreview([]); return; }
      const occs = new RRule({ freq: RRule.WEEKLY, byweekday, dtstart, until }).all();
      setPreview(occs.slice(0, 5));
    } catch {
      setPreview([]);
    }
  }, [isRecurring, selectedDays, dayTimes, recurStartDate, recurEndDate, isInfinite]);

  function toggleDay(key) {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setDayTimes(dt => { const n = { ...dt }; delete n[key]; return n; });
      } else {
        next.add(key);
        setDayTimes(dt => ({ ...dt, [key]: dt[key] || nowTimeStr() }));
      }
      return next;
    });
  }

  async function checkCoachConflicts(coachId, startsAt, durationMinutes, excludeId = null) {
    const endsAt = new Date(new Date(startsAt).getTime() + durationMinutes * 60000).toISOString();
    const { data, error: rpcErr } = await supabase.rpc('check_coach_time_conflicts', {
      p_coach_id: coachId,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
      p_exclude_id: excludeId,
    });
    if (rpcErr) throw rpcErr;
    return data || [];
  }

  async function checkExternalCalendarConflicts(coachId, startsAt, durationMinutes) {
    const endsAt = new Date(new Date(startsAt).getTime() + durationMinutes * 60000).toISOString();
    const { data, error: rpcErr } = await supabase.rpc('check_coach_external_calendar_conflicts', {
      p_coach_id: coachId,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
    });
    if (rpcErr) throw rpcErr;
    return data || [];
  }

  function fmtConflictTime(startsAt, endsAt) {
    const fmt = { timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
    const endFmt = { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hour12: false };
    return `${new Date(startsAt).toLocaleString('nl-NL', fmt)} – ${new Date(endsAt).toLocaleTimeString('nl-NL', endFmt)}`;
  }

  function validate() {
    if (!naam.trim()) { setError(t('lesAanmaken.valNaam')); return false; }
    if (!useCustomLocation && !locationId) { setError(t('lesAanmaken.valLocatie')); return false; }
    if (useCustomLocation && !customLocation.name.trim()) {
      setError(t('lesAanmaken.valCustomLocationName')); return false;
    }
    if (isPT && !clientId) { setError(t('lesAanmaken.valClient')); return false; }
    if (isPT && isPtPlus && !secondClientId) { setError(t('lesAanmaken.valTweedeClient')); return false; }
    if (!parseInt(duur) || parseInt(duur) <= 0) { setError(t('lesAanmaken.valDuur')); return false; }
    if (!isPT && (!parseInt(maxDeelnemers) || parseInt(maxDeelnemers) <= 0)) {
      setError(t('lesAanmaken.valMax')); return false;
    }
    if (isRecurring) {
      if (selectedDays.size === 0) { setError(t('lesAanmaken.valDagen')); return false; }
      if (!isInfinite && !recurEndDate) { setError(t('lesAanmaken.valEinddatum')); return false; }
    }
    return true;
  }

  function buildLesMetadata() {
    const base = {
      name: naam.trim(),
      description: beschrijving.trim() || null,
      location_id: useCustomLocation ? null : locationId,
      // ONDERDEEL 2 (2026-07-26): executing_coach_id nooit NULL laten. coach_id
      // blijft de AANMAKER, executing_coach_id de daadwerkelijke uitvoerder.
      // De cliëntenlijst is hier al gefilterd op de geselecteerde coach, dus de
      // app-bevinding uit onderdeel 3 (default = ingelogde coach i.p.v. de aan
      // de cliënt toegewezen coach) kan in dit scherm niet ontstaan.
      executing_coach_id: coachId || user.id,
      coach_id: user.id,
      type: lesTypeKind,
      duration_minutes: parseInt(duur),
      max_participants: isPT ? 1 : parseInt(maxDeelnemers),
      credit_cost: isWorkshop ? 0 : (parseInt(creditCost) || 0),
      cancel_deadline_hours: parseInt(annuleringstermijn) || 24,
      credit_deduct_moment: 'on_enroll',
      is_trial: isTrial,
    };
    if (isPT && clientId) {
      base.client_id = clientId;
    }
    if (isPT && isPtPlus) {
      base.max_participants = 2;
      const secondName = clients.find(c => c.id === secondClientId)?.full_name || '?';
      const firstName = clients.find(c => c.id === clientId)?.full_name || '?';
      if (!naam.trim() || naam.startsWith('PT')) {
        base.name = `PT+ - ${firstName} & ${secondName}`;
      }
    }
    if (useCustomLocation) {
      base.custom_location_name = customLocation.name.trim();
      base.custom_location_street = customLocation.street.trim() || null;
      base.custom_location_house_number = customLocation.house_number.trim() || null;
      base.custom_location_postal_code = customLocation.postal_code.trim() || null;
      base.custom_location_city = customLocation.city.trim() || null;
    }
    return base;
  }

  function buildOccurrencesWeb() {
    const selectedList = WEEKDAGEN.filter(d => selectedDays.has(d.key));
    const byweekday = selectedList.map(d => d.rruleDay);
    const firstOccs = selectedList.map(d => firstOccurrenceDate(recurStartDate, d.jsIndex));
    const dtstart = firstOccs.reduce((e, o) => o < e ? o : e);
    if (isInfinite) {
      const until = new Date(dtstart.getTime() + 84 * 86400000);
      return {
        rruleStr: new RRule({ freq: RRule.WEEKLY, byweekday, dtstart }).toString(),
        occurrences: new RRule({ freq: RRule.WEEKLY, byweekday, dtstart, until }).all(),
      };
    }
    const until = new Date(toAmsterdamISO(recurEndDate, '23:59'));
    const rule = new RRule({ freq: RRule.WEEKLY, byweekday, dtstart, until });
    return { rruleStr: rule.toString(), occurrences: rule.all() };
  }

  async function sendCoachPushIfNeeded(lesNaam, startsAt) {
    if (!coachId || coachId === user.id) return;
    try {
      const datumStr = new Date(startsAt).toLocaleDateString('nl-NL', {
        timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'long',
      });
      await sendPushToClient(
        supabase, user.id, coachId, 'lessons_schedule',
        t('coachPush.nieuweLes'),
        t('coachPush.nieuweLesBody').replace('{naam}', lesNaam).replace('{datum}', datumStr),
      );
    } catch (err) {
      // Best-effort: de les is op dit punt al aangemaakt, een mislukte push mag
      // dat niet terugdraaien. Wel loggen — stille pushfouten hielden de
      // 403-regressie van 2026-06-22 wekenlang verborgen.
      console.error('Fout bij versturen push naar uitvoerende coach:', err);
    }
  }

  async function checkLessonsHaveEnrollments(lessonIds) {
    // Controleer of er al inschrijvingen zijn op deze lessen
    const { data, error } = await supabase
      .from('lesson_enrollments')
      .select('lesson_id')
      .in('lesson_id', lessonIds)
      .neq('status', 'cancelled');

    if (error) throw error;
    return (data || []).length;
  }

  async function saveLessonAllowedMemberships(lessonIds) {
    // Als selectedMembershipTypes leeg is: geen restrictie (alle abonnementen toegestaan)
    if (selectedMembershipTypes.size === 0) return;

    // Controleer of er al inschrijvingen zijn
    const enrollmentCount = await checkLessonsHaveEnrollments(lessonIds);
    if (enrollmentCount > 0) {
      throw new Error(t('lesAanmaken.valInschrijvingenBestaand')
        .replace('{aantal}', enrollmentCount)
        .replace('{lessen}', lessonIds.length));
    }

    // Maak koppeling tussen lessen en toegestane abonnementstypen
    const rows = [];
    lessonIds.forEach(lessonId => {
      selectedMembershipTypes.forEach(typeId => {
        rows.push({ lesson_id: lessonId, membership_type_id: typeId });
      });
    });

    if (rows.length > 0) {
      const { error } = await supabase.from('lesson_allowed_memberships').insert(rows);
      if (error) throw error;
    }
  }

  async function saveRecurrenceAllowedMemberships(recurrenceId, lessonIds) {
    // Als selectedMembershipTypes leeg is: geen restrictie (alle abonnementen toegestaan)
    if (selectedMembershipTypes.size === 0) return;

    // Controleer of er al inschrijvingen zijn op lessen uit deze reeks
    const enrollmentCount = await checkLessonsHaveEnrollments(lessonIds);
    if (enrollmentCount > 0) {
      throw new Error(t('lesAanmaken.valInschrijvingenBestaand')
        .replace('{aantal}', enrollmentCount)
        .replace('{lessen}', lessonIds.length));
    }

    // Maak koppeling tussen reeks en toegestane abonnementstypen
    const rows = Array.from(selectedMembershipTypes).map(typeId => ({
      recurrence_id: recurrenceId,
      membership_type_id: typeId,
    }));

    const { error } = await supabase.from('recurrence_allowed_memberships').insert(rows);
    if (error) throw error;
  }

  async function executeLesAanmaken() {
    setConflictWarning(null);
    setSaving(true);
    try {
      const metadata = buildLesMetadata();
      if (!isRecurring) {
        const startsAt = toAmsterdamISO(datum, tijd);
        const { data: newLesson, error: insErr } = await supabase
          .from('group_lessons')
          .insert({ ...metadata, starts_at: startsAt })
          .select('id')
          .maybeSingle();
        if (insErr) throw insErr;
        if (!newLesson) throw new Error('Les kon niet worden aangemaakt.');
        // BUG 11: PT-client direct inschrijven zodat roster 1/1 toont
        if (isPT && clientId) {
          const { error: enrollErr } = await supabase.from('lesson_enrollments').insert({
            lesson_id: newLesson.id,
            client_id: clientId,
            status: 'enrolled',
            is_trial: isTrial,
          });
          if (enrollErr) console.error('Inschrijving PT-client mislukt:', enrollErr.message);
          // 1-op-2 PT: tweede client ook inschrijven
          if (isPtPlus && secondClientId) {
            const { error: enrollErr2 } = await supabase.from('lesson_enrollments').insert({
              lesson_id: newLesson.id,
              client_id: secondClientId,
              status: 'enrolled',
              is_trial: false,
            });
            if (enrollErr2) console.error('Inschrijving tweede client mislukt:', enrollErr2.message);
          }
        }
        await saveLessonAllowedMemberships([newLesson.id]);
        await sendCoachPushIfNeeded(naam.trim(), startsAt);
        navigate('/coach/rooster', { state: { toast: t('lesAanmaken.succesAanmaken') } });
      } else {
        const { rruleStr, occurrences } = buildOccurrencesWeb();
        if (occurrences.length === 0) { setError(t('lesAanmaken.geenPreview')); return; }
        const { data: rec, error: recErr } = await supabase
          .from('lesson_recurrences').insert({ rrule: rruleStr, is_infinite: isInfinite }).select('id').maybeSingle();
        if (recErr) throw recErr;
        if (!rec) throw new Error('Herhalingspatroon kon niet worden aangemaakt.');
        const { data: newLessons, error: batchErr } = await supabase
          .from('group_lessons')
          .insert(occurrences.map(occ => ({ ...metadata, starts_at: occurrenceToStartsAt(occ, dayTimes), recurrence_id: rec.id })))
          .select('id');
        if (batchErr) throw batchErr;
        // Sla abonnementsrestricties op (dit controleert ook op bestaande inschrijvingen)
        await saveRecurrenceAllowedMemberships(rec.id, newLessons.map(l => l.id));
        await saveLessonAllowedMemberships(newLessons.map(l => l.id));
        // Onderdeel 5 (2026-07-25): de losse-les-tak schreef de PT-cliënt wél
        // in, deze reeks-tak niet. Gevolg: de lessen toonden de naam op de
        // kaart (via group_lessons.client_id) maar "0/1 deelnemers" in het
        // detailscherm, en de cliënt zag de afspraken nergens — die lijst
        // loopt volledig via lesson_enrollments. Bewust ná de restrictie-
        // opslag: die weigert lessen die al inschrijvingen hebben.
        if (isPT && clientId) {
          const enrollRows = newLessons.map(l => ({
            lesson_id: l.id, client_id: clientId, status: 'enrolled', is_trial: isTrial,
          }));
          if (isPtPlus && secondClientId) {
            newLessons.forEach(l => enrollRows.push({
              lesson_id: l.id, client_id: secondClientId, status: 'enrolled', is_trial: false,
            }));
          }
          const { error: enrollErr } = await supabase.from('lesson_enrollments').insert(enrollRows);
          if (enrollErr) throw new Error(t('lesAanmaken.foutInschrijvenReeks') + enrollErr.message);
        }
        if (occurrences.length > 0) {
          await sendCoachPushIfNeeded(naam.trim(), occurrenceToStartsAt(occurrences[0], dayTimes));
        }
        navigate('/coach/rooster', { state: { toast: t('lesAanmaken.succesReeks') } });
      }
    } catch (err) {
      setError(t('lesAanmaken.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    setError('');
    setConflictWarning(null);
    if (!validate()) return;
    setSaving(true);
    try {
      const dur = parseInt(duur);
      const coachToCheck = coachId || user.id;
      let slots = [];
      if (!isRecurring) {
        slots = [toAmsterdamISO(datum, tijd)];
      } else {
        const { occurrences } = buildOccurrencesWeb();
        if (occurrences.length === 0) { setError(t('lesAanmaken.geenPreview')); setSaving(false); return; }
        slots = occurrences.map(occ => occurrenceToStartsAt(occ, dayTimes));
      }
      const [results, externalResults] = await Promise.all([
        Promise.all(slots.map(sa => checkCoachConflicts(coachToCheck, sa, dur))),
        Promise.all(slots.map(sa => checkExternalCalendarConflicts(coachToCheck, sa, dur))),
      ]);
      const seen = new Set();
      const allConflicts = [];
      for (const items of results) {
        for (const item of items) {
          if (!seen.has(item.conflict_id)) { seen.add(item.conflict_id); allConflicts.push(item); }
        }
      }
      const seenExternal = new Set();
      const allExternal = [];
      for (const items of externalResults) {
        for (const item of items) {
          if (!seenExternal.has(item.conflict_id)) { seenExternal.add(item.conflict_id); allExternal.push(item); }
        }
      }
      // Externe agenda-items (ICS-sync) zijn altijd overrulebaar: ze zijn niet
      // per definitie fysieke afspraken (bv. all-day reminders/verjaardagen).
      const softExternal = allExternal
        .map(c => ({ ...c, conflict_type: 'external', conflict_title: c.conflict_title || t('conflicten.privateAgendaBusy') }));
      const combinedConflicts = [...allConflicts, ...softExternal];
      if (combinedConflicts.length > 0) {
        setSaving(false);
        setConflictWarning({ conflicts: combinedConflicts, onConfirm: executeLesAanmaken });
        return;
      }
      await executeLesAanmaken();
    } catch (err) {
      setError(t('conflicten.checkFout') + err.message);
      setSaving(false);
    }
  }

  if (loadingInit) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost" onClick={() => navigate('/coach/rooster')} style={{ marginBottom: 12 }}>
          {t('lesAanmaken.terug')}
        </button>
        <h1 className="page-title">{t('lesAanmaken.titleNieuw')}</h1>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Basisgegevens */}
      <FormSection title={t('lesAanmaken.sectionBasic')}>
        <Field label={t('lesAanmaken.naamLabel')}>
          <input
            className="form-input"
            value={naam}
            onChange={e => setNaam(e.target.value)}
            placeholder={t('lesAanmaken.naamPlaceholder')}
          />
        </Field>
        <Field label={t('lesAanmaken.beschrijvingLabel')}>
          <textarea
            className="form-input"
            value={beschrijving}
            onChange={e => setBeschrijving(e.target.value)}
            placeholder={t('lesAanmaken.beschrijvingPlaceholder')}
            rows={3}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field label={t('lesAanmaken.locatieLabel')}>
          {locations.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('lesAanmaken.geenLocaties')}</div>
          ) : (
            <select
              className="form-input"
              value={useCustomLocation ? '__custom__' : locationId}
              onChange={e => {
                if (e.target.value === '__custom__') {
                  setUseCustomLocation(true);
                  setLocationId('');
                } else {
                  setUseCustomLocation(false);
                  setLocationId(e.target.value);
                }
              }}
            >
              <option value="">{t('lesFormulier.selecteerLocatie')}</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              <option value="__custom__">{t('lesAanmaken.andereLocatie')}</option>
            </select>
          )}
        </Field>

        {useCustomLocation && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }}>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
              {t('lesAanmaken.andereLocatieHint')}
            </p>
            <Field label={t('admin.locationName') + ' *'}>
              <input
                className="form-input"
                value={customLocation.name}
                onChange={e => setCustomLocation(p => ({ ...p, name: e.target.value }))}
                placeholder={t('admin.locationNamePlaceholder')}
              />
            </Field>
            <Field label={t('admin.locationStreet')}>
              <input
                className="form-input"
                value={customLocation.street}
                onChange={e => setCustomLocation(p => ({ ...p, street: e.target.value }))}
                placeholder={t('admin.locationStreetPlaceholder')}
              />
            </Field>
            <Field label={t('admin.locationHouseNumber')}>
              <input
                className="form-input"
                value={customLocation.house_number}
                onChange={e => setCustomLocation(p => ({ ...p, house_number: e.target.value }))}
                placeholder={t('admin.locationHouseNumberPlaceholder')}
              />
            </Field>
            <Field label={t('admin.locationPostalCode')}>
              <input
                className="form-input"
                value={customLocation.postal_code}
                onChange={e => setCustomLocation(p => ({ ...p, postal_code: e.target.value }))}
                placeholder={t('admin.locationPostalCodePlaceholder')}
              />
            </Field>
            <Field label={t('admin.locationCity')}>
              <input
                className="form-input"
                value={customLocation.city}
                onChange={e => setCustomLocation(p => ({ ...p, city: e.target.value }))}
                placeholder={t('admin.locationCityPlaceholder')}
              />
            </Field>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                if (!customLocation.name.trim()) {
                  setError(t('lesAanmaken.valCustomLocationName'));
                  return;
                }
                pendingSaveLocRef.current = customLocation;
                setSaveLocConfirmOpen(true);
              }}
              style={{ marginTop: 12 }}
            >
              {t('lesAanmaken.opslaanAlsVasteLocatie')}
            </button>
          </div>
        )}
        <Field label={t('lesAanmaken.typeLabel')}>
          <ToggleButtons
            options={[
              { value: 'group', label: t('lesAanmaken.typeGroepsles') },
              { value: 'personal', label: t('lesAanmaken.typePT') },
              { value: 'workshop', label: t('lesAanmaken.typeWorkshop') },
            ]}
            value={lesTypeKind}
            onChange={setLesTypeKind}
          />
        </Field>
        <Field label={t('lesAanmaken.coachLabel')}>
          {coaches.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('lesAanmaken.geenCoaches')}</div>
          ) : (
            <select className="form-input" value={coachId} onChange={e => setCoachId(e.target.value)}>
              <option value="">{t('lesFormulier.selecteerCoach')}</option>
              {coaches.map(c => (
                <option key={c.id} value={c.id}>
                  {c.full_name}{c.id === user?.id ? t('lesFormulier.jij') : ''}
                </option>
              ))}
            </select>
          )}
        </Field>
        {isPT && (
          <>
            <Field label={t('lesAanmaken.clientLabel')}>
              {loadingClients ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('common.laden')}</div>
              ) : clients.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('lesAanmaken.geenClients')}</div>
              ) : (
                <select className="form-input" value={clientId} onChange={e => setClientId(e.target.value)}>
                  <option value="">{t('lesAanmaken.selecteerClient')}</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.full_name}{c.customer_number ? ` (#${c.customer_number})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <input
                type="checkbox"
                id="isTrial"
                checked={isTrial}
                onChange={e => setIsTrial(e.target.checked)}
                style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="isTrial" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--color-text)' }}>
                {t('lesFormulier.markAsTrial')}
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <input
                type="checkbox"
                id="isPtPlus"
                checked={isPtPlus}
                onChange={e => {
                  setIsPtPlus(e.target.checked);
                  if (!e.target.checked) {
                    setSecondClientId('');
                    setPtPlusWarnings([]);
                  }
                }}
                style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="isPtPlus" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--color-text)' }}>
                {t('lesAanmaken.ptPlusToggle')}
              </label>
            </div>
            {isPtPlus && (
              <>
                <Field label={t('lesAanmaken.tweedeClientLabel')}>
                  {loadingClients ? (
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('common.laden')}</div>
                  ) : (
                    <select
                      className="form-input"
                      value={secondClientId}
                      onChange={e => setSecondClientId(e.target.value)}
                    >
                      <option value="">{t('lesAanmaken.selecteerTweedeClient')}</option>
                      {clients.filter(c => c.id !== clientId).map(c => (
                        <option key={c.id} value={c.id}>
                          {c.full_name}{c.customer_number ? ` (#${c.customer_number})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label={t('lesAanmaken.sessieDuurLabel')}>
                  <select
                    className="form-input"
                    value={duur}
                    onChange={e => setDuur(e.target.value)}
                    style={{ maxWidth: 160 }}
                  >
                    <option value="30">30 {t('lesAanmaken.minuten')}</option>
                    <option value="60">60 {t('lesAanmaken.minuten')}</option>
                  </select>
                </Field>
                {ptPlusWarnings.length > 0 && (
                  <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                    {ptPlusWarnings.map((w, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#e65100', marginBottom: i < ptPlusWarnings.length - 1 ? 6 : 0 }}>
                        ⚠ {w.msg}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </FormSection>

      {/* Herhaling */}
      <FormSection title={t('lesAanmaken.sectionHerhaling')}>
        <ToggleButtons
          options={[
            { value: false, label: t('lesAanmaken.eenmalig') },
            { value: true, label: t('lesAanmaken.terugkerend') },
          ]}
          value={isRecurring}
          onChange={setIsRecurring}
          style={{ marginBottom: 16 }}
        />

        {!isRecurring ? (
          <>
            <Field label={t('lesAanmaken.datumLabel')}>
              <input type="date" className="form-input" value={datum} onChange={e => setDatum(e.target.value)} style={{ maxWidth: 200 }} />
            </Field>
            <Field label={t('lesAanmaken.tijdLabel')}>
              <input type="time" className="form-input" value={tijd} onChange={e => setTijd(e.target.value)} style={{ maxWidth: 160 }} />
            </Field>
          </>
        ) : (
          <>
            <Field label={t('lesFormulier.begindatumReeks')}>
              <input type="date" className="form-input" value={recurStartDate} onChange={e => setRecurStartDate(e.target.value)} style={{ maxWidth: 200 }} />
            </Field>
            <Field label={t('lesAanmaken.dagenLabel')}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {WEEKDAGEN.map(d => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    style={{
                      width: 40, height: 40, borderRadius: 20, border: '1px solid',
                      borderColor: selectedDays.has(d.key) ? 'var(--color-primary)' : 'var(--color-border)',
                      background: selectedDays.has(d.key) ? 'var(--color-primary)' : 'transparent',
                      color: selectedDays.has(d.key) ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </Field>
            {WEEKDAGEN.filter(d => selectedDays.has(d.key)).map(d => (
              <Field key={d.key} label={t('lesFormulier.starttijdLabel').replace('{dag}', d.label)}>
                <input
                  type="time"
                  className="form-input"
                  value={dayTimes[d.key] || ''}
                  onChange={e => setDayTimes(prev => ({ ...prev, [d.key]: e.target.value }))}
                  style={{ maxWidth: 160 }}
                />
              </Field>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <input
                type="checkbox"
                id="isInfinite"
                checked={isInfinite}
                onChange={e => setIsInfinite(e.target.checked)}
                style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="isInfinite" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--color-text)' }}>
                {t('lesAanmaken.oneindigLabel')}
              </label>
            </div>
            {!isInfinite && (
              <Field label={t('lesAanmaken.einddatumLabel')}>
                <input type="date" className="form-input" value={recurEndDate} onChange={e => setRecurEndDate(e.target.value)} style={{ maxWidth: 200 }} />
              </Field>
            )}
            {preview.length > 0 && (
              <div style={{
                background: 'var(--color-bg)', borderRadius: 6, padding: '10px 14px',
                marginTop: 4,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('lesAanmaken.previewLabel')}
                </div>
                {preview.map((occ, i) => {
                  const dayKey = getPreviewDayKey(occ);
                  const lesTime = dayTimes[dayKey];
                  return (
                    <div key={i} style={{ fontSize: 13, color: 'var(--color-text)', marginBottom: 4 }}>
                      {format(toAmsterdamDate(occ.toISOString()), 'EEEE d MMMM yyyy', { locale: nl })}
                      {lesTime ? ` · ${lesTime}` : ''}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </FormSection>

      {(coachId || user?.id) && (
        <CoachAvailabilityCalendar
          coachId={coachId || user.id}
          baseDateStr={isRecurring ? recurStartDate : datum}
          selectedStartsAt={!isRecurring && datum && tijd ? toAmsterdamISO(datum, tijd) : null}
          durationMinutes={parseInt(duur, 10) || 60}
          onSelectSlot={(dateStr, timeStr) => {
            if (!isRecurring) {
              setDatum(dateStr);
              setTijd(timeStr);
            }
          }}
        />
      )}

      {/* Duur */}
      <FormSection title={t('lesAanmaken.sectionDatetime')}>
        <Field label={t('lesAanmaken.duurLabel')}>
          <input
            type="number"
            className="form-input"
            value={duur}
            onChange={e => setDuur(e.target.value)}
            placeholder={t('lesAanmaken.duurPlaceholder')}
            min="1"
            style={{ maxWidth: 120 }}
          />
        </Field>
      </FormSection>

      {/* Capaciteit & credits */}
      <FormSection title={t('lesAanmaken.sectionCapacity')}>
        {!isPT && (
          <Field label={t('lesAanmaken.maxDeelnemersLabel')}>
            <input
              type="number"
              className="form-input"
              value={maxDeelnemers}
              onChange={e => setMaxDeelnemers(e.target.value)}
              placeholder={t('lesAanmaken.maxPlaceholder')}
              min="1"
              style={{ maxWidth: 120 }}
            />
          </Field>
        )}
        <Field label={t('lesAanmaken.annuleringstermijnLabel')}>
          <input
            type="number"
            className="form-input"
            value={annuleringstermijn}
            onChange={e => setAnnuleringstermijn(e.target.value)}
            placeholder={t('lesAanmaken.annuleringsPlaceholder')}
            min="0"
            style={{ maxWidth: 120 }}
          />
        </Field>
        {isWorkshop ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {t('lesAanmaken.workshopCreditHint')}
          </p>
        ) : (
          <Field label={t('lesAanmaken.creditCostLabel')}>
            <input
              type="number"
              className="form-input"
              value={creditCost}
              onChange={e => setCreditCost(e.target.value)}
              placeholder={t('lesAanmaken.creditPlaceholder')}
              min="0"
              style={{ maxWidth: 120 }}
            />
          </Field>
        )}
      </FormSection>

      {/* Toegestane abonnementen — verbergen bij PT-les (BUG 10) */}
      {!isPT && <FormSection title={t('lesAanmaken.sectionAbonnementen')}>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14 }}>
          {t('lesAanmaken.abonnementenHint')}
        </p>
        {membershipTypes.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {t('lesAanmaken.geenAbonnementenGevonden')}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="alleAbonnementen"
                checked={selectedMembershipTypes.size === 0}
                onChange={e => {
                  if (e.target.checked) setSelectedMembershipTypes(new Set());
                }}
                style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="alleAbonnementen" style={{ fontSize: 14, cursor: 'pointer', fontWeight: 600, color: 'var(--color-text)' }}>
                {t('lesAanmaken.alleAbonnementen')}
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {membershipTypes.map(type => {
                const isSelected = selectedMembershipTypes.has(type.id);
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setSelectedMembershipTypes(prev => {
                        const next = new Set(prev);
                        if (next.has(type.id)) {
                          next.delete(type.id);
                        } else {
                          next.add(type.id);
                        }
                        return next;
                      });
                    }}
                    style={{
                      padding: '8px 14px', borderRadius: 6, border: '1px solid',
                      borderColor: isSelected ? 'var(--color-primary)' : 'var(--color-border)',
                      background: isSelected ? 'var(--color-primary)' : 'transparent',
                      color: isSelected ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer', fontSize: 13, fontWeight: 500,
                    }}
                  >
                    {type.name}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </FormSection>}

      {conflictWarning && (
        <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#e65100' }}>{t('conflicten.titel')}</div>
          <div style={{ fontSize: 13, marginBottom: 10, color: '#555' }}>{t('conflicten.beschrijving')}</div>
          <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
            {conflictWarning.conflicts.map(c => (
              <li key={c.conflict_id} style={{ fontSize: 13, marginBottom: 4, color: 'var(--color-text)' }}>
                <strong>{c.conflict_type === 'external' ? t('conflicten.typePrivateAgenda') : (c.conflict_type === 'personal' ? t('conflicten.typePT') : t('conflicten.typeGroepsles'))}</strong>
                {': '}{c.conflict_title} — {fmtConflictTime(c.conflict_starts_at, c.conflict_ends_at)}
              </li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              style={{ background: '#c62828', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              onClick={() => conflictWarning.onConfirm()}
            >
              {t('conflicten.bevestigenKnop')}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setConflictWarning(null)}
            >
              {t('conflicten.annulerenKnop')}
            </button>
          </div>
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ width: '100%', padding: '14px', fontSize: 15, marginTop: 8, marginBottom: 32 }}
      >
        {saving ? t('common.laden') : (isRecurring ? t('lesAanmaken.opslaanReeks') : t('lesAanmaken.opslaan'))}
      </button>

      {saveLocConfirmOpen && (
        <ConfirmModal
          title={t('lesAanmaken.opslaanAlsVasteLocatie')}
          body={t('lesAanmaken.opslaanAlsVasteLocatieConfirm')}
          onConfirm={async () => {
            setSaveLocConfirmOpen(false);
            const loc = pendingSaveLocRef.current;
            if (!loc) return;
            try {
              const { data: newLoc, error: err } = await supabase
                .from('locations')
                .insert({
                  name: loc.name.trim(),
                  street: loc.street.trim() || null,
                  house_number: loc.house_number.trim() || null,
                  postal_code: loc.postal_code.trim() || null,
                  city: loc.city.trim() || null,
                })
                .select()
                .maybeSingle();
              if (err) throw err;
              if (!newLoc) throw new Error('Locatie kon niet worden aangemaakt.');
              setLocations(prev => [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name)));
              setLocationId(newLoc.id);
              setUseCustomLocation(false);
              setCustomLocation({ name: '', street: '', house_number: '', postal_code: '', city: '' });
            } catch (err) {
              setError(t('lesAanmaken.foutOpslaanLocatie') + err.message);
            }
          }}
          onClose={() => setSaveLocConfirmOpen(false)}
        />
      )}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16, padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: 'var(--color-text)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 5, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ToggleButtons({ options, value, onChange, style }) {
  return (
    <div style={{ display: 'flex', gap: 8, ...style }}>
      {options.map(opt => (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            padding: '8px 16px', borderRadius: 6, border: '1px solid',
            borderColor: value === opt.value ? 'var(--color-primary)' : 'var(--color-border)',
            background: value === opt.value ? 'var(--color-primary)' : 'transparent',
            color: value === opt.value ? '#fff' : 'var(--color-text)',
            cursor: 'pointer', fontSize: 13, fontWeight: 500,
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
